import React from 'react'
import { useMutation, useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import classNames from 'classnames/bind'
import { useFormatMessage } from '@babylon/intl'
import { Spacer } from '@babylon/ui'
import { useWeightLossSetupNextStepsInfo } from '@/redux/selectors'
import WeightLossImage from '@/assets/images/weight-loss_person-on-scales.jpg'
import MissingFieldsModal from '@/components/Modal/MissingFieldsModal'
import UpdatePatientMutation from '@/mutations/UpdatePatient'
import Template from '../Template'
import Announcement from '@/programmes/components/Announcement'
import messages from '../messages'
import Button from '@/programmes/components/Button'
import { procedures } from '../routes'
import useGetProctorSessionInvite from '@/programmes/hooks/useGetProctorSessionInvite'
import { useTrackingPageRedirect } from '@/programmes/tracking'
import styles from '../index.module.scss'
import { onboardingSteps } from './steps'

const cx = classNames.bind(styles)

const patientQuery = gql`
  query Patient {
    patient {
      id
      date_of_birth
      gender
      phone_number
      region {
        id
        default_phone_country_code
      }
    }
  }
`

const SetupNextStepsPage = () => {
  const t = useFormatMessage()
  const announcement = useWeightLossSetupNextStepsInfo()
  const trackingPageRedirect = useTrackingPageRedirect()
  const { data: patientData, loading: patientQueryLoading } = useQuery(
    patientQuery
  )
  const [updatePatient] = useMutation(UpdatePatientMutation)
  const {
    getProctorSessionInvite,
    data,
    loading,
  } = useGetProctorSessionInvite()
  const proctorInviteUrl = data?.createProctorSessionInvite?.url

  if (proctorInviteUrl) {
    trackingPageRedirect()
    window.location.href = proctorInviteUrl
  }

  return (
    <Template
      title={t(messages.setupNextStepsPageTitle)}
      afterRedirectTitle={
        /* if we are reaching this page after a redirect it means that the user has just paid
           In this case we want to show a different title so Amit Singh can identify the page in the Google Analytics
           Google Analytics doesn't go via the normal Tealium pipleine yet so we don't have the data layer available
           there. This is a temporary solution until we figure out how GA fits into our analytics pipeline
        */
        t(messages.setupNextStepsPaymentCompletePageTitle)
      }
      steps={onboardingSteps}
      activeStep={2}
      loading={patientQueryLoading}
    >
      {patientData && (
        <MissingFieldsModal
          user={patientData.patient}
          checkFields={['date_of_birth', 'gender', 'phone_number']}
          updatePatient={updatePatient}
          useSexAtBirthTerminology
          phoneDescription={t(messages.setupNextStepsPagePhoneDescription)}
          avoidClose
        />
      )}
      <h2 className={cx('heading')}>{t(messages.setupNextStepsPageHeading)}</h2>
      <p className={cx('description')}>
        {t(messages.setupNextStepsPageDescription1)}
      </p>
      <p className={cx('description')}>
        {t(messages.setupNextStepsPageDescription2)}
      </p>
      <p className={cx('description')}>
        {t(messages.setupNextStepsPageDescription3)}
      </p>
      <ul className={cx('requirements')}>
        <li>{t(messages.setupNextStepsPageRequirement1)}</li>
        <li>{t(messages.setupNextStepsPageRequirement2)}</li>
      </ul>
      {announcement && <Announcement content={announcement} />}
      <Spacer bottom={2}>
        <Button
          onClick={() =>
            getProctorSessionInvite({
              variables: {
                input: {
                  procedure: {
                    identifier: { value: procedures.WEIGHT_LOSS_SETUP },
                  },
                },
              },
            })
          }
          loading={loading || !!proctorInviteUrl}
        >
          {t(messages.setupNextStepsPageCta)}
        </Button>
      </Spacer>
      <p className={cx('description')}>
        {t(messages.setupNextStepsPageFooter)}
      </p>
      <Spacer top={2}>
        <img
          className={cx('setup-confirmation-image')}
          src={WeightLossImage}
          alt={t(messages.setupNextStepsPageImageDescription)}
        />
      </Spacer>
    </Template>
  )
}

export default SetupNextStepsPage
